import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toDirectImageUrl, toProxiedImageUrl } from '../utils/mediaProxy';

type ProxyFallbackMode = 'direct' | 'proxy' | 'fallback' | 'failed';

const PROXY_OK_KEY = 'nexusos_proxy_image_prefer_proxy_urls';
const PROXY_BAD_SESSION_KEY = 'nexusos_proxy_image_prefer_direct_urls_session';
const FALLBACK_OK_SESSION_KEY = 'nexusos_proxy_image_prefer_fallback_urls_session';
const MAX_STORED_URLS = 400;

const preferProxyUrls = new Set<string>();
const preferDirectUrlsSession = new Set<string>();
const preferFallbackUrlsSession = new Set<string>();
let storageLoaded = false;

function looksLikeCorpProneIcon(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.includes('apple-touch-icon') ||
    lower.includes('apple-touch') ||
    lower.includes('touch-icon') ||
    lower.includes('apple-icon')
  );
}

function isHostnameOrSubdomain(hostname: string, value: string): boolean {
  return hostname === value || hostname.endsWith(`.${value}`);
}

function shouldForceProxyUrl(url: string): boolean {
  if (looksLikeFaviconIcon(url)) return true;
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    // GitHub avatar URLs are high-volume resources on list pages.
    // Prefer direct load first to avoid saturating proxy-image rate limits.
    if (isHostnameOrSubdomain(hostname, 'github.com')) return false;
    if (hostname.endsWith('githubusercontent.com')) return false;
    if (hostname.endsWith('githubassets.com')) return true;
  } catch {
    return false;
  }
  return false;
}

function shouldPreferFallbackFirst(url: string): boolean {
  const lower = url.toLowerCase();
  if (looksLikeCorpProneIcon(lower)) return true;
  if (lower.includes('favicon-') && (lower.includes('180x180') || lower.includes('192x192') || lower.includes('512x512'))) {
    return true;
  }
  return false;
}

function looksLikeFaviconIcon(url: string): boolean {
  const lower = url.toLowerCase();
  return lower.includes('favicon') || lower.endsWith('.ico') || looksLikeCorpProneIcon(lower);
}

function toDuckDuckGoFaviconUrl(url: string): string | undefined {
  if (!looksLikeFaviconIcon(url)) return undefined;
  try {
    const hostname = new URL(url).hostname;
    if (!hostname) return undefined;
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
  } catch {
    return undefined;
  }
}

function readPreferencesFromStorage(): void {
  if (storageLoaded) return;
  storageLoaded = true;
  if (typeof window === 'undefined') return;

  try {
    const raw = window.localStorage?.getItem(PROXY_OK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((value) => {
          if (typeof value === 'string' && value) preferProxyUrls.add(value);
        });
      }
    }
  } catch {
    // ignore
  }

  try {
    const raw = window.sessionStorage?.getItem(PROXY_BAD_SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((value) => {
          if (typeof value === 'string' && value) preferDirectUrlsSession.add(value);
        });
      }
    }
  } catch {
    // ignore
  }

  try {
    const raw = window.sessionStorage?.getItem(FALLBACK_OK_SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((value) => {
          if (typeof value === 'string' && value) preferFallbackUrlsSession.add(value);
        });
      }
    }
  } catch {
    // ignore
  }
}

function persistPreferProxyUrls(): void {
  if (typeof window === 'undefined') return;
  try {
    const list = Array.from(preferProxyUrls).slice(0, MAX_STORED_URLS);
    window.localStorage?.setItem(PROXY_OK_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

function persistPreferDirectUrlsSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const list = Array.from(preferDirectUrlsSession).slice(0, MAX_STORED_URLS);
    window.sessionStorage?.setItem(PROXY_BAD_SESSION_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

function persistPreferFallbackUrlsSession(): void {
  if (typeof window === 'undefined') return;
  try {
    const list = Array.from(preferFallbackUrlsSession).slice(0, MAX_STORED_URLS);
    window.sessionStorage?.setItem(FALLBACK_OK_SESSION_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export function useImageWithProxyFallback(url: string | null | undefined): {
  src?: string;
  mode: ProxyFallbackMode;
  onError: () => void;
  onLoad: () => void;
} {
  readPreferencesFromStorage();

  const directSrc = useMemo(() => toDirectImageUrl(url), [url]);
  const proxySrc = useMemo(() => toProxiedImageUrl(url), [url]);
  const hasProxyAlternative = Boolean(directSrc && proxySrc && directSrc !== proxySrc);
  const fallbackSrc = useMemo(() => (directSrc ? toDuckDuckGoFaviconUrl(directSrc) : undefined), [directSrc]);
  const fallbackProxySrc = useMemo(() => (fallbackSrc ? toProxiedImageUrl(fallbackSrc) : undefined), [fallbackSrc]);
  const hasFallbackAlternative = Boolean(directSrc && fallbackSrc && fallbackSrc !== directSrc && fallbackSrc !== proxySrc);

  const initialMode: ProxyFallbackMode = useMemo(() => {
    if (!directSrc) return 'failed';
    if (hasFallbackAlternative && preferFallbackUrlsSession.has(directSrc)) return 'fallback';
    if (!hasProxyAlternative) return 'direct';

    if (preferDirectUrlsSession.has(directSrc)) return 'direct';
    if (hasFallbackAlternative && shouldPreferFallbackFirst(directSrc)) return 'fallback';
    if (shouldForceProxyUrl(directSrc)) return 'proxy';
    if (preferProxyUrls.has(directSrc)) return 'proxy';
    return 'direct';
  }, [directSrc, hasProxyAlternative, hasFallbackAlternative]);

  const [mode, setMode] = useState<ProxyFallbackMode>(initialMode);

  const attemptsRef = useRef<{ direct: boolean; proxy: boolean; fallback: boolean }>({
    direct: false,
    proxy: false,
    fallback: false,
  });
  const pendingRef = useRef<{ directFailedForUrl: string | null; proxyFailedForUrl: string | null }>({
    directFailedForUrl: null,
    proxyFailedForUrl: null,
  });

  useEffect(() => {
    setMode(initialMode);
    attemptsRef.current = {
      direct: initialMode === 'direct',
      proxy: initialMode === 'proxy',
      fallback: initialMode === 'fallback',
    };
    pendingRef.current = { directFailedForUrl: null, proxyFailedForUrl: null };
  }, [initialMode, directSrc, proxySrc, fallbackSrc]);

  const src =
    mode === 'proxy'
      ? proxySrc
      : mode === 'fallback'
        ? fallbackProxySrc
        : mode === 'direct'
          ? directSrc
          : undefined;

  const onError = useCallback(() => {
    setMode((current) => {
      const attempts = attemptsRef.current;
      const fallbackAvailable = Boolean(directSrc && fallbackSrc && hasFallbackAlternative);

      if (current === 'direct') {
        if (!directSrc) return 'failed';
        pendingRef.current.directFailedForUrl = directSrc;
        if (hasProxyAlternative && proxySrc && !attempts.proxy) {
          attempts.proxy = true;
          return 'proxy';
        }
        if (fallbackAvailable && !attempts.fallback) {
          attempts.fallback = true;
          return 'fallback';
        }
        return 'failed';
      }

      if (current === 'proxy') {
        if (!directSrc) return 'failed';
        pendingRef.current.proxyFailedForUrl = directSrc;
        if (!attempts.direct) {
          attempts.direct = true;
          return 'direct';
        }
        if (fallbackAvailable && !attempts.fallback) {
          attempts.fallback = true;
          return 'fallback';
        }
        return 'failed';
      }

      if (current === 'fallback') {
        if (hasProxyAlternative && proxySrc && !attempts.proxy) {
          attempts.proxy = true;
          return 'proxy';
        }
        if (!attempts.direct) {
          attempts.direct = true;
          return 'direct';
        }
        return 'failed';
      }

      return 'failed';
    });
  }, [directSrc, proxySrc, hasProxyAlternative, fallbackSrc, hasFallbackAlternative]);

  const onLoad = useCallback(() => {
    if (!directSrc) return;

    if (mode === 'proxy') {
      const directFailedForUrl = pendingRef.current.directFailedForUrl;
      if (directFailedForUrl && directFailedForUrl === directSrc && !preferProxyUrls.has(directSrc)) {
        preferProxyUrls.add(directSrc);
        persistPreferProxyUrls();
      }
      pendingRef.current.directFailedForUrl = null;
      return;
    }

    if (mode === 'direct') {
      const proxyFailedForUrl = pendingRef.current.proxyFailedForUrl;
      if (proxyFailedForUrl && proxyFailedForUrl === directSrc && !preferDirectUrlsSession.has(directSrc)) {
        preferDirectUrlsSession.add(directSrc);
        persistPreferDirectUrlsSession();
      }
      pendingRef.current.directFailedForUrl = null;
      pendingRef.current.proxyFailedForUrl = null;
      return;
    }

    if (mode === 'fallback') {
      const directFailedForUrl = pendingRef.current.directFailedForUrl;
      const proxyFailedForUrl = pendingRef.current.proxyFailedForUrl;
      const shouldPreferFallback =
        hasFallbackAlternative &&
        (directFailedForUrl === directSrc || proxyFailedForUrl === directSrc) &&
        !preferFallbackUrlsSession.has(directSrc);

      if (shouldPreferFallback) {
        preferFallbackUrlsSession.add(directSrc);
        persistPreferFallbackUrlsSession();
      }
      pendingRef.current.directFailedForUrl = null;
      pendingRef.current.proxyFailedForUrl = null;
    }
  }, [directSrc, mode]);

  return { src, mode, onError, onLoad };
}
